import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Upload, Button } from "antd";
import { UploadOutlined } from "@ant-design/icons";

const inputStyle = {
  padding: "6px",
  borderRadius: "4px",
  border: "1px solid #dedede",
  fontSize: "13px",
};

const actionStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "12px",
};

function DetailedInfoForm({ onNext, onPrevious }) {
  const [fileList, setFileList] = useState([]);

  const handleChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const validationSchema = Yup.object({
    Composition: Yup.string().required("Composition is required"),
    TherapeuticName: Yup.string().required("Therapeutic Name is required"),
    GenericName: Yup.string().required("Generic Name is required"),
  });

  const formik = useFormik({
    initialValues: {
      Composition: "",
      TherapeuticName: "",
      GenericName: "",
      AdultDosage: "",
      ChildrenDosage: "",
      Indication: "",
      ContraIndication: "",
      SideEffects: "",
      Precaution: "",
      DrugInteractions: "",
      PregnancyAlert: "",
      LactationAlert: "",
      DrivingAlert: "",
      LiverAlert: "",
      KidneyAlert: "",
      OverdoseSymptomTreatment: "",
      StorageCondition: "",
      Cautions: "",
      LegalText: "",
    },
    validationSchema,
    onSubmit: (values) => {
      const formData = {
        ...values,
        images: fileList.map((file) => file.originFileObj),
      };
      if (onNext) onNext(formData);
    },
  });

  return (
    <form
      style={{ display: "flex", flexDirection: "column", gap: "14px" }}
      onSubmit={formik.handleSubmit}
    >
      <h2 className="justify-content-center" style={{ fontSize: "1.2rem" }}>
        Product Details
      </h2>

      {/* Composition */}
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        <label style={{ fontSize: "14px" }}>
          Composition <span className="text-danger">*</span>
        </label>
        <textarea
          style={{ ...inputStyle, minHeight: "40px" }}
          name="Composition"
          placeholder="Enter composition"
          value={formik.values.Composition}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
        {formik.touched.Composition && formik.errors.Composition && (
          <div style={{ color: "red", fontSize: "11px" }}>
            {formik.errors.Composition}
          </div>
        )}
      </div>

      {/* TherapeuticName & GenericName */}
      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
        <div
          style={{
            flex: 1,
            minWidth: "210px",
            display: "flex",
            flexDirection: "column",
            gap: "6px",
          }}
        >
          <label style={{ fontSize: "14px" }}>
            Therapeutic Name <span className="text-danger">*</span>
          </label>
          <input
            style={inputStyle}
            name="TherapeuticName"
            type="text"
            placeholder="Enter therapeutic name"
            value={formik.values.TherapeuticName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.TherapeuticName && formik.errors.TherapeuticName && (
            <div style={{ color: "red", fontSize: "11px" }}>
              {formik.errors.TherapeuticName}
            </div>
          )}
        </div>

        <div
          style={{
            flex: 1,
            minWidth: "210px",
            display: "flex",
            flexDirection: "column",
            gap: "6px",
          }}
        >
          <label style={{ fontSize: "14px" }}>
            Generic Name <span className="text-danger">*</span>
          </label>
          <input
            style={inputStyle}
            name="GenericName"
            type="text"
            placeholder="Enter generic name"
            value={formik.values.GenericName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.GenericName && formik.errors.GenericName && (
            <div style={{ color: "red", fontSize: "11px" }}>
              {formik.errors.GenericName}
            </div>
          )}
        </div>
      </div>

      {/* AdultDosage & ChildrenDosage */}
      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
        <div
          style={{
            flex: 1,
            minWidth: "210px",
            display: "flex",
            flexDirection: "column",
            gap: "6px",
          }}
        >
          <label style={{ fontSize: "14px" }}>Adult Dosage</label>
          <input
            style={inputStyle}
            name="AdultDosage"
            type="text"
            placeholder="Enter adult dosage"
            value={formik.values.AdultDosage}
            onChange={formik.handleChange}
          />
        </div>

        <div
          style={{
            flex: 1,
            minWidth: "210px",
            display: "flex",
            flexDirection: "column",
            gap: "6px",
          }}
        >
          <label style={{ fontSize: "14px" }}>Children Dosage</label>
          <input
            style={inputStyle}
            name="ChildrenDosage"
            type="text"
            placeholder="Enter children dosage"
            value={formik.values.ChildrenDosage}
            onChange={formik.handleChange}
          />
        </div>
      </div>

      {/* Textarea fields */}
      {[
        "Indication",
        "ContraIndication",
        "SideEffects",
        "Precaution",
        "DrugInteractions",
        "PregnancyAlert",
        "LactationAlert",
        "DrivingAlert",
        "LiverAlert",
        "KidneyAlert",
        "OverdoseSymptomTreatment",
        "StorageCondition",
        "Cautions",
        "LegalText",
      ].map((field) => (
        <div
          key={field}
          style={{ display: "flex", flexDirection: "column", gap: "6px" }}
        >
          <label style={{ fontSize: "14px" }}>
            {field.replace(/([A-Z])/g, " $1").trim()}
          </label>
          <textarea
            style={{ ...inputStyle, minHeight: "40px" }}
            name={field}
            placeholder={`Enter ${field
              .replace(/([A-Z])/g, " $1")
              .trim()
              .toLowerCase()}`}
            value={formik.values[field]}
            onChange={formik.handleChange}
          />
        </div>
      ))}

      {/* --- Product Images Section --- */}
      <div
        style={{
          marginTop: "1.5rem",
          borderTop: "1px solid #eee",
          paddingTop: "1rem",
        }}
      >
        <h4 style={{ marginBottom: "1rem", color: "#1a1a1a" }}>
          Product Images
        </h4>

        <Upload.Dragger
          multiple
          fileList={fileList}
          onChange={handleChange}
          listType="picture"
          beforeUpload={() => false}
          style={{
            border: "1px dashed #6cb3ff",
            background: "#f3faff",
            borderRadius: "8px",
            padding: "1.5rem",
          }}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined style={{ fontSize: "2rem", color: "#00a0dc" }} />
          </p>
          <p className="ant-upload-text" style={{ fontWeight: "bold" }}>
            Upload Images
          </p>
        </Upload.Dragger>

        <div style={{ marginTop: "1rem" }}>
          <Button
            type="primary"
            style={{
              backgroundColor: "#009688",
              borderColor: "#009688",
              fontWeight: "bold",
              borderRadius: "6px",
            }}
            onClick={() => document.querySelector(".ant-upload input").click()}
          >
            Upload Images
          </Button>
          <p
            style={{
              fontSize: "0.85rem",
              color: "#6b7280",
              marginTop: "0.5rem",
            }}
          >
            Set the product media gallery.
          </p>
        </div>
      </div>

      {/* --- Buttons --- */}
      <div style={actionStyle}>
        <button
          type="button"
          className="gradient-dark-button"
          onClick={onPrevious}
        >
          Previous
        </button>
        <button type="submit" className="gradient-button px-3">
          Next
        </button>
      </div>
    </form>
  );
}

export default DetailedInfoForm;
